import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { FileText, Loader2, Sparkles, Copy, Check, Download, Clock, RefreshCw, FileDown } from 'lucide-react';
import { toast } from 'sonner';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

export function MeetingReportGenerator({ submissionId, studentName, studentEmail, token, onReportGenerated }) {
  const [isOpen, setIsOpen] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [docxDownloadUrl, setDocxDownloadUrl] = useState(null);
  const [pdfDownloadUrl, setPdfDownloadUrl] = useState(null);
  const [pdfStatus, setPdfStatus] = useState('none');
  const [reportStatus, setReportStatus] = useState('none'); // none, generating, ready, error
  const [reportEmailStatus, setReportEmailStatus] = useState('none');
  const [reportEmailSentAt, setReportEmailSentAt] = useState(null);
  const [reportEmailError, setReportEmailError] = useState(null);
  const [reportEmailRecipient, setReportEmailRecipient] = useState(studentEmail || '');
  const [filename, setFilename] = useState('');
  const [existingReport, setExistingReport] = useState(null);
  const [isCopied, setIsCopied] = useState(false);
  const [isLoadingExisting, setIsLoadingExisting] = useState(false);
  
  const pollRef = useRef(null);
  const pdfPollRef = useRef(null);

  const buildApiUrl = useCallback((pathOrUrl) => {
    if (!pathOrUrl) return null;
    if (/^https?:\/\//i.test(pathOrUrl)) return pathOrUrl;
    return `${API_BASE}${pathOrUrl}`;
  }, []);

  const checkExistingReport = useCallback(async () => {
    setIsLoadingExisting(true);
    try {
      const res = await fetch(`${API_BASE}/api/admin/submissions/${submissionId}/report`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        if (data.success && data.report_status === 'generating') {
          setReportStatus('generating');
          setIsGenerating(true);
          return;
        }

        if (data.success && data.report_data) {
          setExistingReport(data);
          setReportData(data.report_data);
          setDocxDownloadUrl(buildApiUrl(data.docx_download_url));
          setFilename(data.filename);
          setPdfDownloadUrl(buildApiUrl(data.pdf_download_url));
          setPdfStatus(data.pdf_status || 'none');
          setReportStatus(data.report_status || 'ready');
          setReportEmailStatus(data.report_email_status || 'none');
          setReportEmailSentAt(data.report_email_sent_at || null);
          setReportEmailError(data.report_email_error || null);
          setReportEmailRecipient(data.report_email_recipient || studentEmail || '');
          if (data.transcript) {
            setTranscript(data.transcript);
          }
        }
      }
    } catch (error) {
      console.error('Error checking existing report:', error);
    } finally {
      setIsLoadingExisting(false);
    }
  }, [buildApiUrl, submissionId, token, studentEmail]);

  // Check for existing report when dialog opens
  useEffect(() => {
    if (isOpen) {
      checkExistingReport();
    }
    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
      }
      if (pdfPollRef.current) {
        clearInterval(pdfPollRef.current);
      }
    };
  }, [isOpen, checkExistingReport]);

  // Poll for report status when generating
  useEffect(() => {
    if (reportStatus === 'generating') {
      pollRef.current = setInterval(async () => {
        try {
          const res = await fetch(`${API_BASE}/api/admin/submissions/${submissionId}/report`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (res.ok) {
            const data = await res.json();
            if (data.success && data.report_status === 'ready') {
              setReportData(data.report_data);
              setDocxDownloadUrl(buildApiUrl(data.docx_download_url));
              setPdfDownloadUrl(buildApiUrl(data.pdf_download_url));
              setPdfStatus(data.pdf_status || 'none');
              setFilename(data.filename);
              setExistingReport(data);
              setReportStatus('ready');
              setReportEmailStatus(data.report_email_status || 'none');
              setReportEmailSentAt(data.report_email_sent_at || null);
              setReportEmailError(data.report_email_error || null);
              setReportEmailRecipient(data.report_email_recipient || studentEmail || '');
              setIsGenerating(false);
              toast.success('¡Informe generado correctamente!');
              clearInterval(pollRef.current);
              if (onReportGenerated) {
                onReportGenerated(data);
              }
            } else if (data.report_status === 'error') {
              setReportStatus('error');
              setIsGenerating(false);
              toast.error('Error al generar el informe: ' + (data.report_error || 'Error desconocido'));
              clearInterval(pollRef.current);
            }
            // If still generating, continue polling
          }
        } catch (error) {
          console.error('Error polling report status:', error);
        }
      }, 5000); // Poll every 5 seconds
    }
    
    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
      }
    };
  }, [reportStatus, submissionId, token, onReportGenerated, buildApiUrl, studentEmail]);

  // Continue polling for PDF status after report is ready
  useEffect(() => {
    if (reportStatus === 'ready' && (pdfStatus === 'pending' || pdfStatus === 'generating')) {
      pdfPollRef.current = setInterval(async () => {
        try {
          const res = await fetch(`${API_BASE}/api/admin/submissions/${submissionId}/report`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (res.ok) {
            const data = await res.json();
            if (data.pdf_status === 'ready' && data.pdf_download_url) {
              setPdfDownloadUrl(buildApiUrl(data.pdf_download_url));
              setPdfStatus('ready');
              setReportEmailStatus(data.report_email_status || 'none');
              setReportEmailSentAt(data.report_email_sent_at || null);
              setReportEmailError(data.report_email_error || null);
              setReportEmailRecipient(data.report_email_recipient || studentEmail || '');
              toast.success('PDF listo para descargar');
              clearInterval(pdfPollRef.current);
            } else if (data.pdf_status === 'error') {
              setPdfStatus('error');
              setReportEmailStatus(data.report_email_status || 'none');
              setReportEmailSentAt(data.report_email_sent_at || null);
              setReportEmailError(data.report_email_error || null);
              setReportEmailRecipient(data.report_email_recipient || studentEmail || '');
              clearInterval(pdfPollRef.current);
            } else {
              setReportEmailStatus(data.report_email_status || 'none');
              setReportEmailSentAt(data.report_email_sent_at || null);
              setReportEmailError(data.report_email_error || null);
              setReportEmailRecipient(data.report_email_recipient || studentEmail || '');
            }
          }
        } catch (error) {
          console.error('Error polling PDF status:', error);
        }
      }, 5000);
    }
    return () => {
      if (pdfPollRef.current) clearInterval(pdfPollRef.current);
    };
  }, [reportStatus, pdfStatus, submissionId, token, buildApiUrl, studentEmail]);

  const handleGenerate = async () => {
    if (!transcript.trim()) {
      toast.error('Por favor, pegá la transcripción de la reunión');
      return;
    }

    setIsGenerating(true);
    setReportStatus('generating');
    setPdfStatus('none');
    setReportEmailStatus('pending');
    setReportEmailSentAt(null);
    setReportEmailError(null);
    setReportEmailRecipient(studentEmail || '');
    setPdfDownloadUrl(null);
    setReportData(null);
    setDocxDownloadUrl(null);
    
    try {
      const res = await fetch(`${API_BASE}/api/admin/submissions/${submissionId}/generate-report`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          submission_id: submissionId,
          transcript: transcript,
          student_name: studentName
        })
      });

      const data = await res.json();
      
      if (res.ok && data.success) {
        toast.info('Generación iniciada. Esto puede tardar unos minutos...');
        // Polling will handle the rest
      } else {
        setIsGenerating(false);
        setReportStatus('error');
        toast.error(data.detail || data.error || 'Error al iniciar la generación');
      }
    } catch (error) {
      console.error('Error starting report generation:', error);
      setIsGenerating(false);
      setReportStatus('error');
      toast.error('Error al conectar con el servidor');
    }
  };

  const downloadBinary = async (url, extension) => {
    if (!url) return false;
    try {
      const res = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('No se pudo descargar el archivo');

      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = (filename || `Informe_Reunion_${studentName?.replace(/\s+/g, '_') || 'Alumno'}`) + extension;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(blobUrl);
      return true;
    } catch (error) {
      toast.error(error.message || 'Error al descargar archivo');
      return false;
    }
  };

  const handleDownloadDocx = async () => {
    if (!docxDownloadUrl) {
      toast.error('No hay documento disponible');
      return;
    }
    const ok = await downloadBinary(docxDownloadUrl, '.docx');
    if (ok) toast.success('Documento Word descargado');
  };

  const handleDownloadPdf = async () => {
    if (!pdfDownloadUrl) {
      if (pdfStatus === 'generating' || pdfStatus === 'pending') {
        toast.info('El PDF se está generando, esperá unos segundos...');
      } else {
        toast.error('PDF no disponible. Descargá el Word y exportalo a PDF.');
      }
      return;
    }
    const ok = await downloadBinary(pdfDownloadUrl, '.pdf');
    if (ok) toast.success('Documento PDF descargado');
  };

  const handleCopyText = async () => {
    if (!reportData) return;
    
    let text = '';
    if (reportData.titulo) text += `${reportData.titulo}\n\n`;
    if (reportData.resumen_reunion) text += `RESUMEN DE LA REUNIÓN\n${reportData.resumen_reunion}\n\n`;
    
    if (reportData.perfil_alumno) {
      text += `PERFIL DEL ALUMNO/A\n`;
      if (reportData.perfil_alumno.datos_basicos) text += `${reportData.perfil_alumno.datos_basicos}\n`;
      if (reportData.perfil_alumno.fortalezas) text += `Fortalezas: ${reportData.perfil_alumno.fortalezas.join(', ')}\n`;
      if (reportData.perfil_alumno.areas_mejora) text += `Áreas de mejora: ${reportData.perfil_alumno.areas_mejora.join(', ')}\n`;
      text += '\n';
    }
    
    if (reportData.recomendaciones) {
      text += `RECOMENDACIONES\n`;
      reportData.recomendaciones.forEach((rec, i) => {
        text += `${i + 1}. ${rec.titulo}\n${rec.descripcion}\n`;
        if (rec.protocolo) text += `Protocolo: ${rec.protocolo}\n`;
        text += '\n';
      });
    }
    
    try {
      await navigator.clipboard.writeText(text);
      setIsCopied(true);
      toast.success('Contenido copiado al portapapeles');
      setTimeout(() => setIsCopied(false), 2000);
    } catch (err) {
      toast.error('Error al copiar');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleString('es-AR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getPdfButtonContent = () => {
    if (pdfStatus === 'generating' || pdfStatus === 'pending') {
      return <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Generando...</>;
    }
    if (pdfStatus === 'ready') {
      return <><FileDown className="w-4 h-4 mr-2" />PDF</>;
    }
    return <><FileDown className="w-4 h-4 mr-2" />PDF (no disponible)</>;
  };

  const renderEmailStatus = () => {
    const recipient = reportEmailRecipient || studentEmail || 'el alumno';

    if (reportEmailStatus === 'sent') {
      return (
        <div className="flex items-center gap-2 p-3 bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800 rounded-lg">
          <Check className="w-4 h-4 text-green-500" />
          <span className="text-sm text-green-700 dark:text-green-300">
            PDF enviado por email a {recipient}{reportEmailSentAt ? ` el ${formatDate(reportEmailSentAt)}` : ''}.
          </span>
        </div>
      );
    }

    if (reportEmailStatus === 'failed') {
      return (
        <div className="flex items-center gap-2 p-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg">
          <Clock className="w-4 h-4 text-red-500" />
          <span className="text-sm text-red-700 dark:text-red-300">
            El informe no se pudo enviar por email{reportEmailError ? `: ${reportEmailError}` : '.'}
          </span>
        </div>
      );
    }

    if (['pending', 'sending'].includes(reportEmailStatus) || (reportStatus === 'ready' && pdfStatus === 'pending')) {
      return (
        <div className="flex items-center gap-2 p-3 bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-800 rounded-lg">
          <Loader2 className="w-4 h-4 animate-spin text-amber-500" />
          <span className="text-sm text-amber-700 dark:text-amber-300">
            Cuando el PDF termine de generarse, se enviará automáticamente por email a {recipient}.
          </span>
        </div>
      );
    }

    return null;
  };

  const renderReportPreview = () => {
    if (!reportData) return null;

    return (
      <div className="space-y-4 text-sm">
        <h3 className="font-bold text-lg text-primary">{reportData.titulo}</h3>

        {reportData.resumen_reunion && (
          <div>
            <h4 className="font-semibold">📋 Resumen de la Reunión</h4>
            <p className="text-muted-foreground whitespace-pre-wrap">{reportData.resumen_reunion}</p>
          </div>
        )}

        {reportData.perfil_alumno && (
          <div>
            <h4 className="font-semibold">👤 Perfil del Alumno</h4>
            {reportData.perfil_alumno.datos_basicos && (
              <p className="text-muted-foreground whitespace-pre-wrap">{reportData.perfil_alumno.datos_basicos}</p>
            )}
            {reportData.perfil_alumno.fortalezas && reportData.perfil_alumno.fortalezas.length > 0 && (
              <div className="mt-2">
                <span className="font-medium text-xs">Fortalezas:</span>
                <ul className="list-disc pl-4 text-muted-foreground">
                  {reportData.perfil_alumno.fortalezas.map((f, i) => <li key={i}>{f}</li>)}
                </ul>
              </div>
            )}
            {reportData.perfil_alumno.areas_mejora && reportData.perfil_alumno.areas_mejora.length > 0 && (
              <div className="mt-2">
                <span className="font-medium text-xs">Áreas de Mejora:</span>
                <ul className="list-disc pl-4 text-muted-foreground">
                  {reportData.perfil_alumno.areas_mejora.map((a, i) => <li key={i}>{a}</li>)}
                </ul>
              </div>
            )}
          </div>
        )}

        {reportData.analisis_rueda && (
          <div>
            <h4 className="font-semibold">🎯 Análisis de la Rueda</h4>
            <p className="text-muted-foreground whitespace-pre-wrap">
              {typeof reportData.analisis_rueda === 'string' ? reportData.analisis_rueda : JSON.stringify(reportData.analisis_rueda, null, 2)}
            </p>
          </div>
        )}

        {reportData.plan_accion && (
          <div>
            <h4 className="font-semibold">📝 Plan de Acción</h4>
            <p className="text-muted-foreground whitespace-pre-wrap">
              {typeof reportData.plan_accion === 'string' ? reportData.plan_accion : JSON.stringify(reportData.plan_accion, null, 2)}
            </p>
          </div>
        )}

        {reportData.recomendaciones && reportData.recomendaciones.length > 0 && (
          <div>
            <h4 className="font-semibold">💡 Recomendaciones ({reportData.recomendaciones.length})</h4>
            <div className="space-y-3">
              {reportData.recomendaciones.map((rec, i) => (
                <div key={i} className="p-3 bg-muted/50 rounded-lg">
                  <p className="font-medium text-primary">{i + 1}. {rec.titulo}</p>
                  {rec.descripcion && <p className="text-muted-foreground mt-1 whitespace-pre-wrap">{rec.descripcion}</p>}
                  {rec.protocolo && (
                    <p className="text-xs mt-1 text-blue-600 dark:text-blue-400">Protocolo: {rec.protocolo}</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {reportData.cierre && (
          <div>
            <h4 className="font-semibold">🔚 Cierre</h4>
            <p className="text-muted-foreground whitespace-pre-wrap">{reportData.cierre}</p>
          </div>
        )}

        {reportData.proximos_pasos && (
          <div>
            <h4 className="font-semibold">➡️ Próximos Pasos</h4>
            <p className="text-muted-foreground whitespace-pre-wrap">
              {typeof reportData.proximos_pasos === 'string' ? reportData.proximos_pasos :
               Array.isArray(reportData.proximos_pasos) ? reportData.proximos_pasos.join('\n') :
               JSON.stringify(reportData.proximos_pasos, null, 2)}
            </p>
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <Button 
        variant="default" 
        onClick={() => setIsOpen(true)}
        className="bg-purple-600 hover:bg-purple-700"
        data-testid="generate-report-btn"
      >
        <FileText className="w-4 h-4 mr-2" />
        Generar Informe
      </Button>

      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-purple-500" />
              Generador de Informe de Reunión
            </DialogTitle>
            <DialogDescription>
              Pegá la transcripción de la reunión con {studentName || 'el alumno/a'} para generar un informe profesional.
            </DialogDescription>
          </DialogHeader>

          {isLoadingExisting ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-primary" />
            </div>
          ) : (
            <div className="flex-1 overflow-hidden flex flex-col gap-4">
              {/* Status indicator */}
              {reportStatus === 'generating' && (
                <div className="flex items-center gap-2 p-3 bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 rounded-lg">
                  <Loader2 className="w-4 h-4 animate-spin text-blue-500" />
                  <span className="text-sm text-blue-700 dark:text-blue-300">
                    Generando informe con Claude AI... Esto puede tardar 2-5 minutos.
                  </span>
                </div>
              )}
              
              {/* Existing report notice */}
              {existingReport && existingReport.generated_at && reportStatus === 'ready' && (
                <div className="flex items-center gap-2 p-3 bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800 rounded-lg">
                  <Check className="w-4 h-4 text-green-500" />
                  <span className="text-sm text-green-700 dark:text-green-300">
                    Informe generado el {formatDate(existingReport.generated_at)}
                  </span>
                  <Badge variant="secondary" className="ml-auto">Guardado</Badge>
                </div>
              )}

              {reportStatus === 'ready' && renderEmailStatus()}

              {/* Transcript input */}
              <div className="space-y-2">
                <Label htmlFor="transcript" className="flex items-center gap-2">
                  Transcripción de la Reunión
                  {!reportData && <span className="text-red-500">*</span>}
                </Label>
                <Textarea
                  id="transcript"
                  value={transcript}
                  onChange={(e) => setTranscript(e.target.value)}
                  placeholder="Pegá aquí la transcripción completa de la reunión..."
                  rows={reportData ? 4 : 8}
                  className="resize-none"
                  disabled={isGenerating}
                  data-testid="transcript-input"
                />
              </div>

              {/* Generate button */}
              <Button 
                onClick={handleGenerate} 
                disabled={isGenerating || !transcript.trim()}
                className="bg-purple-600 hover:bg-purple-700"
                data-testid="generate-btn"
              >
                {isGenerating ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Generando... (puede tardar 2-5 min)
                  </>
                ) : (
                  <>
                    {existingReport ? <RefreshCw className="w-4 h-4 mr-2" /> : <Sparkles className="w-4 h-4 mr-2" />}
                    {existingReport ? 'Regenerar Informe' : 'Generar Informe'}
                  </>
                )}
              </Button>

              {/* Report preview and download */}
              {reportData && reportStatus === 'ready' && (
                <>
                  <Separator />
                  
                  <div className="flex items-center justify-between">
                    <Label className="text-base font-semibold flex items-center gap-2">
                      <FileText className="w-4 h-4" />
                      Informe Generado
                    </Label>
                    <div className="flex gap-2">
                      <Button 
                        variant="default" 
                        onClick={handleDownloadDocx}
                        className="bg-blue-600 hover:bg-blue-700"
                        data-testid="download-docx-btn"
                      >
                        <FileDown className="w-4 h-4 mr-2" />
                        Word
                      </Button>
                      <Button 
                        variant="default" 
                        onClick={handleDownloadPdf}
                        disabled={pdfStatus !== 'ready'}
                        className={pdfStatus === 'ready' ? "bg-red-600 hover:bg-red-700" : "bg-gray-400"}
                        data-testid="download-pdf-btn"
                      >
                        {getPdfButtonContent()}
                      </Button>
                      <Button variant="outline" size="sm" onClick={handleCopyText}>
                        {isCopied ? <Check className="w-4 h-4 mr-1 text-green-500" /> : <Copy className="w-4 h-4 mr-1" />}
                        {isCopied ? 'Copiado!' : 'Copiar'}
                      </Button>
                    </div>
                  </div>
                  
                  <div className="flex-1 border rounded-lg p-4 bg-muted/30 overflow-y-auto" style={{ maxHeight: '60vh' }}>
                    {renderReportPreview()}
                  </div>
                </>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

export default MeetingReportGenerator;
